<template>
  <v-card class="card-pad mx-auto">
    <v-dialog v-model="alterDialog" max-width="300px">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Os dados informados não serão salvos, deseja continuar?') }}
          </div>

          <v-btn
            depressed
            :disabled="loading"
            class="ma-1"
            color="grey"
            text
            @click="alterDialog = false"
          >
            {{ $t('Não') }}
          </v-btn>

          <v-btn
            depressed
            :loading="loading"
            class="ma-1"
            color="#B14949"
            text
            @click="closeDialog"
          >
            {{ $t('Sim') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <v-btn absolute top right depressed dark icon @click="close">
      <v-icon color="#bbb">mdi-close</v-icon>
    </v-btn>

    <div class="d-flex align-center justify-center my-3">
      <h2>{{ $t(currentTitle) }}</h2>
      <v-alert
        type="error"
        v-show="error"
        transition="scale-transition"
        icon="mdi-alert-octagon-outline"
      >
        {{ $t('errorMessage') }}
      </v-alert>
    </div>

    <div class="tabs-container mt-3 mb-3">
      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
        grow
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tab
          active-class="activated-tab"
          class="rounded-lg rounded-b-0 tab-button"
          key="equipments"
        >
          <v-icon left small>mdi-message-text</v-icon>
          Equipamento
        </v-tab>

        <v-tab
          key="calibrations"
          active-class="activated-tab"
          class="rounded-lg rounded-b-0 tab-button"
        >
          <v-icon left small>mdi-cog</v-icon>
          Configuração
        </v-tab>
      </v-tabs>
    </div>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-window v-model="step" touchless>
          <v-window-item :value="0">
            <v-form
              class="form"
              ref="firstForm"
              v-model="firstValid"
              lazy-validation
              :disabled="institutionInactive"
            >
              <v-row class="ma-0" dense v-show="institutionInactive">
                <v-col class="py-0" cols="12">
                  <InstitutionInactive />
                </v-col>
              </v-row>
              <v-row class="ma-0 mx-6" dense justify="end">
                <v-col cols="12" sm="6" md="6" lg="6">
                  <label class="label ml-1">{{ $t('Equipamento') }}</label>
                  <v-text-field
                    hide-details="auto"
                    v-model="currentStandardEquipment.name"
                    :rules="nameRules"
                    required
                    outlined
                    class="primary-input"
                  >
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="6" lg="6">
                  <label class="label ml-1">{{ $t('Sigla') }}</label>
                  <v-text-field
                    hide-details="auto"
                    v-model="currentStandardEquipment.initials"
                    :rules="initialsRules"
                    required
                    outlined
                    class="primary-input"
                  >
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" :sm="6">
                  <label class="label ml-1">{{ $t('Função') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="currentStandardEquipment.functionEquipment"
                    :items="functionsEquipment"
                    :rules="[
                      (v) =>
                        this.validateFunctionEquipment(v) || 'Função requerida',
                    ]"
                    outlined
                    class="primary-input"
                    @change="changeFunctionEquipment"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>

                    <template v-slot:item="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" :sm="6">
                  <label class="label ml-1">{{ $t('Departamento') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="currentStandardEquipment.workshopType"
                    :items="workshopTypes"
                    :disabled="
                      institution.departmentTypes.length == 1 &&
                      currentStandardEquipment.workshopType !=
                        'Engenharia clínica'
                    "
                    :rules="[(v) => !!v || 'Departamento requerido']"
                    outlined
                    class="primary-input"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>

                    <template v-slot:item="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" sm="6" md="6" lg="6">
                  <label class="label ml-1">{{ $t('Risco') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="currentStandardEquipment.equipmentRisk"
                    :items="equipmentRisks"
                    :rules="[
                      (v) => this.validateEquipmentRisk(v) || 'Risco requerido',
                    ]"
                    item-text="text"
                    item-value="value"
                    outlined
                    class="primary-input"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>

                    <template v-slot:item="{ item }">
                      <span class="text-caption">
                        {{ $t(item) }}
                      </span>
                    </template>
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" sm="3" md="3" lg="3">
                  <label class="label ml-1">{{ $t('Índice ABC') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="currentStandardEquipment.indexAbc"
                    :items="indexAbc"
                    :rules="[
                      (v) => this.validateIndexAbc(v) || 'Índice ABC requerido',
                    ]"
                    outlined
                    class="primary-input"
                  >
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" sm="3" md="3" lg="3">
                  <label class="label ml-1">Exp. Vida</label>
                  <v-text-field
                    hide-details="auto"
                    v-model="currentStandardEquipment.lifeExpectancy"
                    :rules="lifeExpectancyRules"
                    type="number"
                    required
                    outlined
                    class="primary-input"
                  >
                    <template #message="{ message }">
                      {{ $t(message) }}
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-window-item>

          <v-window-item :value="1">
            <div class="fill-height text-center pa-8">
              <v-icon size="60" color="success">
                mdi-checkbox-marked-circle
              </v-icon>

              <span style="display: block; margin-top: 10px; font-size: 1.2rem">
                {{
                  isEditing
                    ? $t('Equipamento atualizado com sucesso!')
                    : $t('Equipamento cadastrado com sucesso!')
                }}
              </span>
            </div>
          </v-window-item>
        </v-window>
      </v-tab-item>

      <v-tab-item>
        <v-form
          class="form"
          ref="calibrationForm"
          lazy-validation
          :disabled="institutionInactive"
        >
          <v-row class="ma-0" dense v-show="institutionInactive">
            <v-col class="py-0" cols="12">
              <InstitutionInactive />
            </v-col>
          </v-row>
          <v-row class="ma-0 mx-6" dense>
            <v-col cols="12" sm="6" class="d-flex align-start justify-start">
              <v-switch
                inset
                v-model="currentStandardEquipment.calibrationSet"
                hide-details
                :label="$t('Calibração')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-start justify-start">
              <v-switch
                inset
                v-model="currentStandardEquipment.electricalEngineeringSet"
                hide-details
                :label="$t('Segurança elétrica')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6">
              <label
                class="label ml-1"
                v-show="currentStandardEquipment.calibrationSet"
                >{{ $t('Período Calibração') }}</label
              >
              <v-select
                v-show="currentStandardEquipment.calibrationSet"
                hide-details="auto"
                v-model="currentStandardEquipment.calibrationSchedulePeriod"
                :items="schedulePeriods"
                item-text="text"
                item-value="value"
                :rules="[(v) => v != null || 'Período requerido']"
                outlined
                class="primary-input"
              >
                <template v-slot:selection="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>
              </v-select>
              <label
                class="label ml-1"
                :style="{ display: 'inline-block' }"
                v-if="
                  currentStandardEquipment.calibrationSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                >{{ $t('Padrão metrológico de calibração') }}</label
              >
              <v-autocomplete
                v-if="
                  currentStandardEquipment.calibrationSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                v-model="currentStandardEquipment.calibrations"
                :items="equipments"
                item-text="name"
                item-value="id"
                multiple
                clearable
                chips
                small-chips
                deletable-chips
                outlined
                hide-details="auto"
                class="primary-input"
              >
                <template v-slot:selection="{ item, attrs, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    color="#BBCF20"
                    text-color="#fff"
                    small
                  >
                    {{ $t(item.name) }}
                  </v-chip>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.name) }}
                  </span>
                </template>
              </v-autocomplete>
              <v-switch
                v-if="currentStandardEquipment.calibrationSet"
                inset
                v-model="currentStandardEquipment.calibrationSchedule"
                hide-details
                :label="$t('Agendar serviço para o último dia do mês')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6">
              <label
                class="label ml-1"
                v-show="currentStandardEquipment.electricalEngineeringSet"
                >{{ $t('Período Segurança Elétrica') }}</label
              >
              <v-select
                v-show="currentStandardEquipment.electricalEngineeringSet"
                hide-details="auto"
                v-model="
                  currentStandardEquipment.electricalEngineeringSchedulePeriod
                "
                :items="schedulePeriods"
                :rules="[(v) => v != null || 'Período requerido']"
                outlined
                item-text="text"
                item-value="value"
                class="primary-input"
              >
                <template v-slot:selection="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>
              </v-select>
              <label
                class="label ml-1"
                :style="{ display: 'inline-block' }"
                v-if="
                  currentStandardEquipment.electricalEngineeringSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                >{{ $t('Padrão metrológico de segurança elétrica') }}</label
              >
              <v-autocomplete
                v-if="
                  currentStandardEquipment.electricalEngineeringSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                v-model="currentStandardEquipment.electricalSafeties"
                :items="equipments"
                item-text="name"
                item-value="id"
                multiple
                chips
                small-chips
                clearable
                outlined
                hide-details="auto"
                class="primary-input"
              >
                <template v-slot:selection="{ item, attrs, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    color="#BBCF20"
                    text-color="#fff"
                    small
                  >
                    {{ $t(item.name) }}
                  </v-chip>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.name) }}
                  </span>
                </template>
              </v-autocomplete>
              <v-switch
                v-if="currentStandardEquipment.electricalEngineeringSet"
                inset
                v-model="currentStandardEquipment.electricalEngineeringSchedule"
                hide-details
                :label="$t('Agendar serviço para o último dia do mês')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12">
              <v-divider class="ma-2 mt-6"></v-divider>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-start justify-start">
              <v-switch
                inset
                v-model="currentStandardEquipment.validationQualificationSet"
                hide-details
                :label="$t('Validação / Qualificação')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-start justify-start">
              <v-switch
                inset
                v-model="currentStandardEquipment.preventiveSet"
                hide-details
                :label="$t('Preventiva')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6">
              <label
                class="label ml-1"
                v-show="currentStandardEquipment.validationQualificationSet"
                >{{ $t('Periodo Validação / Qualificação') }}</label
              >
              <v-select
                v-show="currentStandardEquipment.validationQualificationSet"
                hide-details="auto"
                v-model="currentStandardEquipment.validationSchedulePeriod"
                :items="schedulePeriods"
                item-text="text"
                item-value="value"
                :rules="[(v) => v != null || 'Período requerido']"
                outlined
                class="primary-input"
              >
                <template v-slot:selection="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>
              </v-select>
              <label
                class="label ml-1"
                :style="{ display: 'inline-block' }"
                v-if="
                  currentStandardEquipment.validationQualificationSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                >{{ $t('Padrão metrológico de validação/qualificação') }}</label
              >
              <v-autocomplete
                v-if="
                  currentStandardEquipment.validationQualificationSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                v-model="currentStandardEquipment.validations"
                :items="equipments"
                item-text="name"
                item-value="id"
                multiple
                clearable
                chips
                small-chips
                :rules="[(v) => v != null || 'Período requerido']"
                outlined
                hide-details="auto"
                class="primary-input"
              >
                <template v-slot:selection="{ item, attrs, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    color="#BBCF20"
                    text-color="#fff"
                    small
                  >
                    {{ $t(item.name) }}
                  </v-chip>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.name) }}
                  </span>
                </template>
              </v-autocomplete>
              <v-switch
                v-if="currentStandardEquipment.validationQualificationSet"
                inset
                v-model="
                  currentStandardEquipment.validationQualificationSchedule
                "
                hide-details
                :label="$t('Agendar serviço para o último dia do mês')"
                color="#bbcf20"
              ></v-switch>
            </v-col>

            <v-col cols="12" sm="6">
              <label
                class="label ml-1"
                v-show="currentStandardEquipment.preventiveSet"
              >
                {{ $t('Periodo Preventiva') }}
              </label>
              <v-select
                v-show="currentStandardEquipment.preventiveSet"
                hide-details="auto"
                v-model="currentStandardEquipment.preventiveSchedulePeriod"
                :items="preventiveSchedulePeriods"
                item-text="text"
                item-value="value"
                outlined
                class="primary-input"
              >
                <template v-slot:selection="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.text) }}
                  </span>
                </template>
              </v-select>
              <label
                class="label ml-1"
                :style="{ display: 'inline-block' }"
                v-if="
                  currentStandardEquipment.preventiveSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                >{{ $t('Padrão metrológico de preventiva') }}</label
              >
              <v-autocomplete
                v-if="
                  currentStandardEquipment.preventiveSet &&
                  currentStandardEquipment.functionEquipment ===
                    'Simulador/Analisador/Padrão'
                "
                v-model="currentStandardEquipment.preventives"
                :items="equipments"
                item-text="name"
                item-value="id"
                multiple
                clearable
                chips
                small-chips
                outlined
                hide-details="auto"
                class="primary-input"
              >
                <template v-slot:selection="{ item, attrs, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    color="#BBCF20"
                    text-color="#fff"
                    small
                  >
                    {{ $t(item.name) }}
                  </v-chip>
                </template>

                <template v-slot:item="{ item }">
                  <span class="text-caption">
                    {{ $t(item.name) }}
                  </span>
                </template>
              </v-autocomplete>
              <v-switch
                v-if="currentStandardEquipment.preventiveSet"
                inset
                v-model="currentStandardEquipment.preventiveSchedule"
                hide-details
                :label="$t('Agendar serviço para o último dia do mês')"
                color="#bbcf20"
              ></v-switch>
            </v-col>
          </v-row>
        </v-form>
      </v-tab-item>
    </v-tabs-items>

    <v-divider class="mb-6 mt-2 mx-8"></v-divider>

    <div class="d-flex mx-6" v-if="!institutionInactive">
      <v-btn
        depressed
        @click="close"
        v-if="step === 0"
        class="secondary-button"
      >
        Cancelar
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        depressed
        class="primary-button mr-0"
        @click="next"
        :loading="loading"
      >
        {{ $t(buttonText) }}
      </v-btn>
    </div>
  </v-card>
</template>

<style scoped>
.v-application .primary--text {
  color: #707070 !important;
  caret-color: #707070 !important;
}

@media (max-width: 600px) {
  .v-card__text {
    padding: 0;
  }
}
</style>

<script>
import ApiService from '../../../../services/ApiService'
import InstitutionInactive from '../../Institutions/components/institutionInactive/institutionInactive.vue'

export default {
  components: {
    InstitutionInactive,
  },

  props: {
    title: String,
    standardEquipment: Object,
    fromAdmin: String,
  },

  created() {
    this.currentTitle = this.title
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.user = JSON.parse(localStorage.getItem('user'))
    this.institutionInactive = this.institution.status == 1

    if (this.institution) {
      this.currentStandardEquipment.workshopType =
        this.workshopTypes[this.institution.departmentTypes[0]]
    }

    if (this.standardEquipment) {
      this.currentStandardEquipment = this.standardEquipment
      this.currentStandardEquipment.preventiveSchedulePeriod = this
        .standardEquipment.preventiveSchedulePeriod
        ? this.standardEquipment.preventiveSchedulePeriod
        : 4

      if (this.currentStandardEquipment.calibrationSchedulePeriod == null) {
        this.currentStandardEquipment.calibrationSchedulePeriod = 4
      }
      if (
        this.currentStandardEquipment.electricalEngineeringSchedulePeriod ==
        null
      ) {
        this.currentStandardEquipment.electricalEngineeringSchedulePeriod = 4
      }
      if (this.currentStandardEquipment.validationSchedulePeriod == null) {
        this.currentStandardEquipment.validationSchedulePeriod = 4
      }
      if (this.currentStandardEquipment.preventiveSchedulePeriod == null) {
        this.currentStandardEquipment.preventiveSchedulePeriod = 4
      }

      if (!this.isNumber(this.standardEquipment.functionEquipment)) {
        this.standardEquipment.functionEquipment =
          this.functionsEquipment.indexOf(
            this.standardEquipment.functionEquipment
          )
      }

      if (!this.isNumber(this.standardEquipment.equipmentRisk)) {
        this.standardEquipment.equipmentRisk = this.equipmentRisks.indexOf(
          this.standardEquipment.equipmentRisk
        )
      }

      if (!this.isNumber(this.standardEquipment.workshopType)) {
        this.standardEquipment.workshopType = this.workshopTypes.indexOf(
          this.standardEquipment.workshopType
        )
      }

      this.currentStandardEquipment.functionEquipment =
        this.functionsEquipment[this.standardEquipment.functionEquipment]
      this.currentStandardEquipment.equipmentRisk =
        this.equipmentRisks[this.standardEquipment.equipmentRisk]
      this.currentStandardEquipment.workshopType =
        this.workshopTypes[this.standardEquipment.workshopType]

      this.isEditing = true

      if (this.fromAdmin == 0) {
        this.currentStandardEquipment.calibrationSet =
          this.currentStandardEquipment.calibrationSet != null
            ? this.currentStandardEquipment.calibrationSet
            : this.currentStandardEquipment.standardEquipment.calibrationSet
        this.currentStandardEquipment.electricalEngineeringSet = this
          .currentStandardEquipment.electricalEngineeringSet
          ? this.currentStandardEquipment.electricalEngineeringSet
          : this.currentStandardEquipment.standardEquipment
              .electricalEngineeringSet

        this.currentStandardEquipment.validationQualificationSet =
          this.currentStandardEquipment.validationQualificationSet != null
            ? this.currentStandardEquipment.validationQualificationSet
            : this.currentStandardEquipment.standardEquipment
                .validationQualificationSet

        this.currentStandardEquipment.preventiveSet =
          this.currentStandardEquipment.preventiveSet != null
            ? this.currentStandardEquipment.preventiveSet
            : this.currentStandardEquipment.standardEquipment.preventiveSet
      }
    }

    this.getWorkshops()
    if (this.standardEquipment) {
      this.setupStandardEquipment()
    }
    this.getEquipments()
  },

  data: () => ({
    user: {},
    workshops: null,
    workshopsLoading: false,
    activeTab: 0,
    schedulePeriods: [
      {
        text: 'Mensal',
        value: 0,
      },
      {
        text: 'Bimestral',
        value: 1,
      },
      {
        text: 'Trimestral',
        value: 2,
      },
      {
        text: 'Semestral',
        value: 3,
      },
      {
        text: 'Anual',
        value: 4,
      },
      {
        text: 'Bienal',
        value: 5,
      },
    ],
    preventiveSchedulePeriods: [
      {
        text: 'Mensal',
        value: 0,
      },
      {
        text: 'Bimestral',
        value: 1,
      },
      {
        text: 'Trimestral',
        value: 2,
      },
      {
        text: 'Semestral',
        value: 3,
      },
      {
        text: 'Anual',
        value: 4,
      },
      {
        text: 'Bienal',
        value: 5,
      },
      {
        text: 'Usar MTBF',
        value: 6,
      },
    ],
    currentTitle: '',
    loading: false,
    step: 0,
    firstValid: true,
    alterDialog: false,
    changedData: false,
    error: false,
    errorMessage: '',
    isEditing: false,
    institutionInactive: false,
    date: null,
    institution: {},
    equipments: [],
    functionsEquipment: [
      'Acessório',
      'Análise',
      'Diagnóstico',
      'Outros equipamentos de apoio',
      'Suporte à vida',
      'Terapia',
      'Simulador/Analisador/Padrão',
    ],
    equipmentRisks: [
      'Acessório',
      'Injuria',
      'Morte',
      'Sem risco',
      'Terapia ou diagnóstico inapropriado',
    ],
    indexAbc: ['A', 'B', 'C'],
    workshopTypes: ['Engenharia clínica', 'Manutenção predial'],
    currentStandardEquipment: {
      id: 0,
      preventiveSet: 4,
      electricalEngineeringSchedulePeriod: 4,
      calibrationSchedulePeriod: 4,
      validationSchedulePeriod: 4,
      preventiveSchedulePeriod: 4,
      name: '',
      initials: '',
      functionEquipment: null,
      equipmentRisk: null,
      indexAbc: null,
      lifeExpectancy: 0,
      workshopType: null,
      calibrations: [],
      validations: [],
      electricalSafeties: [],
      preventives: [],
    },
    nameRules: [
      (v) => !!v || 'Nome requerido',
      (v) => (v && v.length <= 128) || 'Nome deve ter no máximo 128 caracteres',
    ],
    initialsRules: [
      (v) => !!v || 'Sigla requerida',
      (v) => (v && v.length <= 10) || 'Sigla deve ter no máximo 10 caracteres',
    ],
    lifeExpectancyRules: [(v) => !!v || 'Exp.Vida requerida'],

    apiService: new ApiService(),
  }),

  computed: {
    buttonText() {
      switch (this.step) {
        case 0:
          return this.isEditing ? 'Atualizar' : 'Adicionar'
        default:
          return 'Terminar'
      }
    },
  },

  methods: {
    async getWorkshops() {
      const institutionId = JSON.parse(localStorage.getItem('institution')).id
      this.workshopsLoading = true
      try {
        const response = await this.apiService.get(`workshop/${institutionId}`)
        this.workshops = response.content
        this.workshopsLoading = false
      } catch (error) {
        this.workshopsLoading = false
      } finally {
        this.workshopsLoading = false
      }
    },

    close() {
      if (this.changedData) {
        this.alterDialog = true
      } else {
        this.$emit('close')
      }
    },

    closeDialog() {
      this.changedData = false
      this.close()
    },

    isNumber(value) {
      return typeof value === 'number'
    },

    changeFunctionEquipment() {
      const isSimulator =
        this.currentStandardEquipment.functionEquipment ===
        'Simulador/Analisador/Padrão'

      if (isSimulator) {
        if (!this.isEditing) {
          this.currentStandardEquipment.calibrationSet = true
          this.currentStandardEquipment.electricalEngineeringSet = true
          this.currentStandardEquipment.validationQualificationSet = true
          this.currentStandardEquipment.preventiveSet = true

          if (this.currentStandardEquipment.calibrationSchedulePeriod == null) {
            this.currentStandardEquipment.calibrationSchedulePeriod = 4
          }
          if (
            this.currentStandardEquipment.electricalEngineeringSchedulePeriod ==
            null
          ) {
            this.currentStandardEquipment.electricalEngineeringSchedulePeriod = 4
          }
          if (this.currentStandardEquipment.validationSchedulePeriod == null) {
            this.currentStandardEquipment.validationSchedulePeriod = 4
          }
          if (this.currentStandardEquipment.preventiveSchedulePeriod == null) {
            this.currentStandardEquipment.preventiveSchedulePeriod = 4
          }
        }
      } else {
        this.currentStandardEquipment.calibrationSet = false
        this.currentStandardEquipment.electricalEngineeringSet = false
        this.currentStandardEquipment.validationQualificationSet = false
        this.currentStandardEquipment.preventiveSet = false

        this.currentStandardEquipment.calibrations = []
        this.currentStandardEquipment.electricalSafeties = []
        this.currentStandardEquipment.validations = []
        this.currentStandardEquipment.preventives = []
      }
    },

    validateFunctionEquipment(value) {
      return this.functionsEquipment.indexOf(value) !== -1
    },

    validateEquipmentRisk(value) {
      return this.equipmentRisks.indexOf(value) !== -1
    },

    validateIndexAbc(value) {
      return this.indexAbc.indexOf(value) !== -1
    },

    async next() {
      if (this.step === 0) {
        if (this.$refs.firstForm.validate()) {
          await this.submit()
          if (!this.error) {
            if (this.isEditing) {
              this.closeDialog()
            } else {
              this.step += 1
            }
          }
        }
      } else {
        this.closeDialog()
      }
    },

    async submit() {
      this.loading = true

      let model = {}

      if (this.fromAdmin == 1 || !this.isEditing) {
        model = {
          StandardEquipment: {
            Id: this.currentStandardEquipment.id,
            Name: this.currentStandardEquipment.name,
            Initials: this.currentStandardEquipment.initials,
            FunctionEquipment: this.functionsEquipment.indexOf(
              this.currentStandardEquipment.functionEquipment
            ),
            EquipmentRisk: this.equipmentRisks.indexOf(
              this.currentStandardEquipment.equipmentRisk
            ),
            IndexAbc: this.currentStandardEquipment.indexAbc,
            LifeExpectancy: this.currentStandardEquipment.lifeExpectancy,
            WorkshopType: this.workshopTypes.indexOf(
              this.currentStandardEquipment.workshopType
            ),
            IsFromAdmin: this.fromAdmin == 1,
            CalibrationSet:
              this.currentStandardEquipment.calibrationSet ?? false,
            ElectricalEngineeringSet:
              this.currentStandardEquipment.electricalEngineeringSet ?? false,
            ValidationQualificationSet:
              this.currentStandardEquipment.validationQualificationSet ?? false,
            PreventiveSet: this.currentStandardEquipment.preventiveSet ?? false,
            CalibrationSchedulePeriod:
              this.currentStandardEquipment.calibrationSchedulePeriod,
            ElectricalEngineeringSchedulePeriod:
              this.currentStandardEquipment.electricalEngineeringSchedulePeriod,
            ValidationSchedulePeriod:
              this.currentStandardEquipment.validationSchedulePeriod,
            PreventiveSchedulePeriod:
              this.currentStandardEquipment.preventiveSchedulePeriod,
            CalibrationSchedule:
              this.currentStandardEquipment.calibrationSchedule ?? false,
            ElectricalEngineeringSchedule:
              this.currentStandardEquipment.electricalEngineeringSchedule ??
              false,
            ValidationQualificationSchedule:
              this.currentStandardEquipment.validationQualificationSchedule ??
              false,
            PreventiveSchedule:
              this.currentStandardEquipment.preventiveSchedule ?? false,
            Calibrations: this.currentStandardEquipment.calibrations.map(
              (id) => ({
                CalibrationEquipmentId: id,
              })
            ),
            Validations: this.currentStandardEquipment.validations.map(
              (id) => ({
                ValidationEquipmentId: id,
              })
            ),
            ElectricalSafeties:
              this.currentStandardEquipment.electricalSafeties.map((id) => ({
                ElectricalSafetyEquipmentId: id,
              })),
            Preventives: this.currentStandardEquipment.preventives.map(
              (id) => ({
                PreventiveEquipmentId: id,
              })
            ),
          },
          InstitutionId: this.institution.id,
        }
      } else {
        model = {
          Id: this.currentStandardEquipment.id,
          Name: this.currentStandardEquipment.name,
          Initials: this.currentStandardEquipment.initials,
          FunctionEquipment: this.functionsEquipment.indexOf(
            this.currentStandardEquipment.functionEquipment
          ),
          EquipmentRisk: this.equipmentRisks.indexOf(
            this.currentStandardEquipment.equipmentRisk
          ),
          IndexAbc: this.currentStandardEquipment.indexAbc,
          LifeExpectancy: this.currentStandardEquipment.lifeExpectancy,
          WorkshopType: this.workshopTypes.indexOf(
            this.currentStandardEquipment.workshopType
          ),
          IsFromAdmin: this.fromAdmin == 1,
          CalibrationSet: this.currentStandardEquipment.calibrationSet ?? false,
          ElectricalEngineeringSet:
            this.currentStandardEquipment.electricalEngineeringSet ?? false,
          ValidationQualificationSet:
            this.currentStandardEquipment.validationQualificationSet ?? false,
          PreventiveSet: this.currentStandardEquipment.preventiveSet ?? false,
          CalibrationSchedulePeriod:
            this.currentStandardEquipment.calibrationSchedulePeriod,
          ElectricalEngineeringSchedulePeriod:
            this.currentStandardEquipment.electricalEngineeringSchedulePeriod,
          ValidationSchedulePeriod:
            this.currentStandardEquipment.validationSchedulePeriod,
          PreventiveSchedulePeriod:
            this.currentStandardEquipment.preventiveSchedulePeriod,
          CalibrationSchedule:
            this.currentStandardEquipment.calibrationSchedule ?? false,
          ElectricalEngineeringSchedule:
            this.currentStandardEquipment.electricalEngineeringSchedule ??
            false,
          ValidationQualificationSchedule:
            this.currentStandardEquipment.validationQualificationSchedule ??
            false,
          PreventiveSchedule:
            this.currentStandardEquipment.preventiveSchedule ?? false,
          InstitutionId: this.institution.id,
          Calibrations: this.currentStandardEquipment.calibrations.map(
            (id) => ({
              CalibrationEquipmentId: id,
            })
          ),
          Validations: this.currentStandardEquipment.validations.map((id) => ({
            ValidationEquipmentId: id,
          })),
          ElectricalSafeties:
            this.currentStandardEquipment.electricalSafeties.map((id) => ({
              ElectricalSafetyEquipmentId: id,
            })),
          Preventives: this.currentStandardEquipment.preventives.map((id) => ({
            PreventiveEquipmentId: id,
          })),
        }
      }

      let url = 'standardEquipment/add'

      if (this.isEditing) {
        url = 'standardEquipment/edit'
      }

      if (this.fromAdmin == 0 && this.isEditing) {
        url = 'institutionStandardEquipment/edit'
      }

      await this.apiService
        .post(url, model)
        .then(() => {})
        .catch((err) => {
          this.errorMessage = err.body.message
          this.error = true

          setTimeout(() => {
            this.error = false
          }, 4000)
        })

      this.loading = false
      this.changedData = false
    },

    async remove() {
      this.loading = true

      await this.apiService
        .delete('unidade/delete', this.currentUnidade)
        .then(() => {
          this.updateTable()
          this.loading = false
          this.deleteDialog = false
        })
        .catch(() => {
          this.loading = false
          this.deleteDialog = false
        })
    },

    async getEquipments() {
      try {
        if (this.fromAdmin == 0) {
          const response = await this.apiService.get(
            `institutionStandardEquipment/list/${this.institution.id}`,
            {
              institutionId: this.institution.id,
            }
          )
          this.equipments = response.content
        } else {
          const response = await this.apiService.get(`standardEquipment/list`)
          this.equipments = response.content
        }
      } catch (error) {
        console.error('Erro ao buscar equipamento:', error)
      }
    },

    setupStandardEquipment() {
      this.currentStandardEquipment = { ...this.standardEquipment }

      if (this.currentStandardEquipment.calibrationSchedulePeriod == null) {
        this.currentStandardEquipment.calibrationSchedulePeriod = 4
      }
      if (
        this.currentStandardEquipment.electricalEngineeringSchedulePeriod ==
        null
      ) {
        this.currentStandardEquipment.electricalEngineeringSchedulePeriod = 4
      }
      if (this.currentStandardEquipment.validationSchedulePeriod == null) {
        this.currentStandardEquipment.validationSchedulePeriod = 4
      }
      if (this.currentStandardEquipment.preventiveSchedulePeriod == null) {
        this.currentStandardEquipment.preventiveSchedulePeriod = 4
      }

      if (!this.isNumber(this.standardEquipment.functionEquipment)) {
        this.standardEquipment.functionEquipment =
          this.functionsEquipment.indexOf(
            this.standardEquipment.functionEquipment
          )
      }

      if (!this.isNumber(this.standardEquipment.equipmentRisk)) {
        this.standardEquipment.equipmentRisk = this.equipmentRisks.indexOf(
          this.standardEquipment.equipmentRisk
        )
      }

      if (!this.isNumber(this.standardEquipment.workshopType)) {
        this.standardEquipment.workshopType = this.workshopTypes.indexOf(
          this.standardEquipment.workshopType
        )
      }

      this.currentStandardEquipment.functionEquipment =
        this.functionsEquipment[this.standardEquipment.functionEquipment]
      this.currentStandardEquipment.equipmentRisk =
        this.equipmentRisks[this.standardEquipment.equipmentRisk]
      this.currentStandardEquipment.workshopType =
        this.workshopTypes[this.standardEquipment.workshopType]

      this.currentStandardEquipment.calibrations =
        this.standardEquipment.calibrations?.map(
          (c) => c.calibrationEquipmentId
        ) || []

      this.currentStandardEquipment.validations =
        this.standardEquipment.validations?.map(
          (v) => v.validationEquipmentId
        ) || []

      this.currentStandardEquipment.electricalSafeties =
        this.standardEquipment.electricalSafeties?.map(
          (e) => e.electricalSafetyEquipmentId
        ) || []

      this.currentStandardEquipment.preventives =
        this.standardEquipment.preventives?.map(
          (p) => p.preventiveEquipmentId
        ) || []

      this.isEditing = true

      if (this.fromAdmin == 0) {
        this.currentStandardEquipment.calibrationSet =
          this.currentStandardEquipment.calibrationSet != null
            ? this.currentStandardEquipment.calibrationSet
            : this.currentStandardEquipment.standardEquipment.calibrationSet
        this.currentStandardEquipment.electricalEngineeringSet = this
          .currentStandardEquipment.electricalEngineeringSet
          ? this.currentStandardEquipment.electricalEngineeringSet
          : this.currentStandardEquipment.standardEquipment
              .electricalEngineeringSet

        this.currentStandardEquipment.validationQualificationSet =
          this.currentStandardEquipment.validationQualificationSet != null
            ? this.currentStandardEquipment.validationQualificationSet
            : this.currentStandardEquipment.standardEquipment
                .validationQualificationSet

        this.currentStandardEquipment.preventiveSet =
          this.currentStandardEquipment.preventiveSet != null
            ? this.currentStandardEquipment.preventiveSet
            : this.currentStandardEquipment.standardEquipment.preventiveSet
      }
    },
  },

  watch: {
    currentStandardEquipment: {
      deep: true,
      handler() {
        this.changedData = true
      },
    },
  },
}
</script>
